/* ============================================
   QUOTE WORKSTATION — Core
   Asset: workstation-core.js

   Namespace creation, state, DOM refs, toast,
   pricing engine, dropdown utilities, constants.
   Loaded first via <script defer>.
   ============================================ */
(function() {
  'use strict';

  function whenReady(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  whenReady(function() {
    if (!window.RepPortal) return;
    var API = window.RepPortal.API;
    var Auth = window.RepPortal.Auth;

    var wrap = document.querySelector('.rep-quote-portal-wrap');
    if (!wrap) return;

    /* ── Toast notification system ── */
    var toastContainer = document.createElement('div');
    toastContainer.className = 'rq-ws-toast-container';
    wrap.appendChild(toastContainer);

    if (!document.getElementById('rq-ws-toast-css')) {
      var toastStyle = document.createElement('style');
      toastStyle.id = 'rq-ws-toast-css';
      toastStyle.textContent = [
        '.rq-ws-toast-container{position:fixed;top:20px;left:50%;transform:translateX(-50%);z-index:100000;display:flex;flex-direction:column;align-items:center;gap:8px;pointer-events:none;}',
        '.rq-ws-toast{pointer-events:auto;max-width:480px;min-width:280px;padding:14px 20px;border-radius:8px;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;font-size:14px;line-height:1.5;box-shadow:0 4px 24px rgba(0,0,0,0.15);opacity:0;transform:translateY(-12px);transition:opacity 0.25s ease,transform 0.25s ease;white-space:pre-line;word-break:break-word;}',
        '.rq-ws-toast--visible{opacity:1;transform:translateY(0);}',
        '.rq-ws-toast--error{background:#fff;border:1px solid var(--rq-error-border,rgba(220,38,38,0.2));color:var(--rq-error,#dc2626);}',
        '.rq-ws-toast--success{background:#fff;border:1px solid rgba(22,163,74,0.2);color:var(--rq-success,#16a34a);}',
        '.rq-ws-toast--warning{background:#fff;border:1px solid rgba(217,119,6,0.2);color:#b45309;}',
        '.rep-quote-dark .rq-ws-toast--error{background:#1a1a2e;border-color:var(--rq-error-border,rgba(248,113,113,0.25));color:var(--rq-error,#f87171);}',
        '.rep-quote-dark .rq-ws-toast--success{background:#1a1a2e;border-color:rgba(52,211,153,0.25);color:var(--rq-success,#34d399);}',
        '.rep-quote-dark .rq-ws-toast--warning{background:#1a1a2e;border-color:rgba(251,191,36,0.25);color:#fbbf24;}',
        /* CUSTOM_TOASTS_BY_CLAUDE (Ryan 2026-10-25) - 'info' was asked for by name and never styled (it showed as bare text) */
        '.rq-ws-toast--info{background:#fff;border:1px solid rgba(100,116,139,0.25);color:#334155;}',
        '.rep-quote-dark .rq-ws-toast--info{background:#1a1a2e;border-color:rgba(148,163,184,0.3);color:#e2e8f0;}',
        '.rq-ws-toast__dismiss{position:absolute;top:6px;right:10px;background:none;border:none;font-size:18px;cursor:pointer;color:inherit;opacity:0.5;padding:2px 4px;line-height:1;}',
        '.rq-ws-toast__dismiss:hover{opacity:1;}',
        '.rq-ws-toast--has-dismiss{padding-right:36px;position:relative;}',
        '.rq-ws-variant-picker__pill--unavailable{opacity:0.25;pointer-events:none;}'
      ].join('\n');
      document.head.appendChild(toastStyle);
    }

    /* CUSTOM_TOASTS_BY_CLAUDE (Ryan 2026-10-25: the freight-request toast "doesn't stay long enough to read it, and it's
       red, implying an error") - (1) a toast stays up long enough to READ it: about 60 ms a character on top of 1.5 s,
       never under 3.5 s, capped at 12 s, and never shorter than a duration the caller asked for; the mouse over it holds
       it. (2) No type is NEUTRAL ('info'), never red: every call names its colour - success green, warning amber ("done,
       but look"), error red (it failed or was blocked). An unnamed one used to default to red, which is how a success
       went out as an error. */
    function toastDuration(message, asked) {
      var readable = Math.min(12000, Math.max(3500, 1500 + 60 * String(message || '').length));
      return Math.max(asked || 0, readable);
    }
    function showToast(message, type, duration) {
      type = type || 'info';
      var isMultiLine = message.indexOf('\n') !== -1;
      duration = toastDuration(message, duration);

      var toast = document.createElement('div');
      toast.className = 'rq-ws-toast rq-ws-toast--' + type;
      if (isMultiLine || duration > 5000) {
        toast.classList.add('rq-ws-toast--has-dismiss');
        var dismissBtn = document.createElement('button');
        dismissBtn.className = 'rq-ws-toast__dismiss';
        dismissBtn.innerHTML = '&times;';
        dismissBtn.setAttribute('type', 'button');
        dismissBtn.addEventListener('click', function() { removeToast(toast); });
        toast.appendChild(dismissBtn);
      }
      var textNode = document.createElement('span');
      textNode.textContent = message;
      toast.appendChild(textNode);
      toastContainer.appendChild(toast);

      requestAnimationFrame(function() { toast.classList.add('rq-ws-toast--visible'); });

      var timer = setTimeout(function() { removeToast(toast); }, duration);
      toast._timer = timer;
      /* the mouse over it holds it; leaving gives a couple of seconds more */
      toast.addEventListener('mouseenter', function() { clearTimeout(toast._timer); });
      toast.addEventListener('mouseleave', function() { if (!toast._removed) toast._timer = setTimeout(function() { removeToast(toast); }, 2500); });
    }

    function removeToast(toast) {
      if (toast._removed) return;
      toast._removed = true;
      clearTimeout(toast._timer);
      toast.classList.remove('rq-ws-toast--visible');
      setTimeout(function() { if (toast.parentNode) toast.parentNode.removeChild(toast); }, 300);
    }

    /* ── Swatch tooltip ── */
    var swatchTip = document.createElement('div');
    swatchTip.className = 'rq-ws-swatch-tooltip';
    document.body.appendChild(swatchTip);
    wrap.addEventListener('mouseover', function(e) {
      var pill = e.target.closest('.rq-ws-variant-picker__pill--swatch[data-label]');
      if (!pill) { swatchTip.style.opacity = '0'; return; }
      swatchTip.textContent = pill.dataset.label;
      swatchTip.style.opacity = '1';
      var r = pill.getBoundingClientRect();
      var tw = swatchTip.offsetWidth;
      var left = r.left + r.width / 2 - tw / 2;
      if (left < 4) left = 4;
      if (left + tw > window.innerWidth - 4) left = window.innerWidth - 4 - tw;
      swatchTip.style.left = left + 'px';
      swatchTip.style.top = (r.bottom + 6) + 'px';
    });
    wrap.addEventListener('mouseout', function(e) {
      if (e.target.closest('.rq-ws-variant-picker__pill--swatch')) swatchTip.style.opacity = '0';
    });

    /* ── State ── */
    var seriesQtyPropagated = {};
    var collapsedVendors = {};
    var state = {
      mode: 'new',
      locked: false,
      ref: null,
      draftOrderId: '',
      items: [],
      fees: [
        { title: 'Shipping & Freight', amount: 0, status: 'pending' },
        /* CUSTOM_QWINSTALLNONE_BY_CLAUDE (Ryan 2026-10-11) — a new quote starts at None for
           installation; most deals have none. Kept in step with state.installation.status below,
           the reset in workstation-customer.js and the worker's lead/Add-to-Quote records. */
        { title: 'Installation', amount: 0, status: 'none' }
      ],
      customFees: [],
      /* CUSTOM_RFQPORTAL_BY_CLAUDE — sku -> external (non-BSF) product URL for the
         PDF product-links feature. Populated from /product-external-link when the
         "Add website links to products" toggle is on. */
      externalLinks: {},
      onBsf: {},
      bsfUrls: {},
      linkProductsOnPdf: false,
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-09-01) — what the QUOTE calls a vendor, keyed by the
         vendor's canonical name. Display only: `item.vendor` never becomes a byline, because that
         string is a state key for grouping, freight, pallet fees and PO membership. */
      vendorLabels: {},
      // CUSTOM_ROOMDESIGNER_BY_CLAUDE — append linked gym layout to the quote PDF (per-space pages)
      includeLayoutInPdf: false,
      layoutOverage: false,      // show overage on the appended layout page (o1 variant)
      layoutDimensions: false,   // show wall dimensions on the appended layout page (w1 variant)
      linkedFiles: [],
      tax: { mode: 'pending', rate: 0, amount: 0, includeFreight: false, includeInstallation: false },
      cardFee: { status: 'pending', amount: 0 },
      freight: {
        status: 'pending',
        requestedAt: null, requestedBy: null,
        vendors: [],
        blanketMarkup: false, blanketMarkupPct: 10,
        totalCost: 0, totalQuoted: 0,
        appliedAt: null,
        history: [],
        itemsSnapshot: [],
        // Destination conditions — set by the rep at quote-build time, used by
        // logistics to quote freight accurately. Cascades to per-vendor flags
        // when the rep changes them and when new vendors are added.
        deliveryConditions: { insideDelivery: false, loadingDock: false, forkLift: false, freightInsurance: true, defaultDelivery: true }
      },
      installation: {
        status: 'none', // CUSTOM_QWINSTALLNONE_BY_CLAUDE — see fees[1] above
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-26) - installation scope:
           '' | 'flooring' | 'equipment'. One string, so the two checkboxes cannot
           both be on. Empty is the default and prints nothing on the quote. */
        scope: '',
        requestedAt: null, requestedBy: null,
        companies: [],
        totalCost: 0, totalQuoted: 0,
        appliedAt: null,
        history: []
      },
      send: { terms: 'Pay in Full', leadTime: '', expiration: '' },
      /* CUSTOM_RFQPORTAL_BY_CLAUDE — Per-quote Sales Rep override.
         Admin-only ghost-author feature: substitutes the printed
         Sales Rep block on the customer PDF (name/phone/email) without
         touching ownership, ref, commission, or dashboard attribution.
         Any field blank → falls through to the default rep info. */
      salesRepOverride: { name: '', phone: '', email: '' },
      /* CUSTOM_RFQPORTAL_BY_CLAUDE — Admin-only existing credit.
         Tracks an amount the customer has already paid (e.g. deposit on
         an earlier quote in a linked order chain). Renders as a negative
         line in totals + reduces the payment link total via draft order
         applied_discount. sourceRef is an optional pointer back to the
         originating quote for audit. */
      existingCredit: { amount: 0, sourceRef: '' },
      leadSource: '',
      // Referral / share recipient — only meaningful when leadSource === 'shared'.
      // The named person earns commission at gpPercent of GP-of-Goods, regardless
      // of whether the rep on this quote takes a cut themselves.
      referral: { name: '', gpPercent: 0 }, // BLOCK A — Shared Deal recipient (paid)
      // BLOCK B — referral source (who referred the deal); tracking only, no payout. RETIRED as an
      // input 2026-07-24 (DNI decoupling piece 3); kept in state so a legacy value round-trips.
      referralSource: { name: '' },
      cfaAttribution: '',
      // CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-07-09) — manual "Repeat buyer" override.
      // Auto-detect (prior SOLD deal by email/phone) comes from the load response;
      // this is the rep's manual force-on. Comp-neutral relationship marker.
      repeatBuyerOverride: false,
      quotesCache: null,
      dirty: false,
      pdfDownloaded: false,
      // Once-per-quote E-Cleaner prompt tracking. Sticky across saves and
      // reloads — set to true the first time the prompt fires (whether the
      // rep adds the cleaner or skips). Two flags so save and download each
      // get their own independent "one last chance" nudge.
      eCleanerPromptedOnSave: false,
      eCleanerPromptedOnDownload: false,
      loadedGrandTotal: null,
      lastGrandTotal: 0
    };

    var d30 = new Date();
    d30.setDate(d30.getDate() + 30);
    state.send.expiration = d30.toISOString().split('T')[0];

    /* ── DOM Refs ── */
    var dom = {};
    dom.itemsContainer = wrap.querySelector('.rq-ws-items');
    dom.itemsEmpty = wrap.querySelector('.rq-ws-items__empty');
    dom.totalsPanel = wrap.querySelector('.rq-ws-totals');
    dom.totals = {
      msrp: wrap.querySelector('.rq-ws-totals__msrp'),
      discount: wrap.querySelector('.rq-ws-totals__discount'),
      direct: wrap.querySelector('.rq-ws-totals__direct'),
      shipping: wrap.querySelector('.rq-ws-totals__shipping'),
      installation: wrap.querySelector('.rq-ws-totals__installation'),
      tax: wrap.querySelector('.rq-ws-totals__tax'),
      grand: wrap.querySelector('.rq-ws-totals__grand'),
      margin: wrap.querySelector('.rq-ws-totals__margin')
    };
    dom.searchInput = wrap.querySelector('.rq-ws-search-bar__input');
    dom.searchResults = wrap.querySelector('.rq-ws-search-bar__results');
    dom.barCustomer = wrap.querySelector('.rq-ws-bar__customer');
    dom.barRef = wrap.querySelector('.rq-ws-bar__ref');
    dom.barStatus = wrap.querySelector('.rq-ws-bar__status');
    dom.btnNew = wrap.querySelector('.rq-ws-bar__btn--new');
    dom.btnLoad = wrap.querySelector('.rq-ws-bar__btn--load');
    dom.loadDropdown = wrap.querySelector('.rq-ws-bar__load-dropdown');
    dom.loadSearch = wrap.querySelector('.rq-ws-bar__load-search');
    dom.loadResults = wrap.querySelector('.rq-ws-bar__load-results');
    dom.feesCustomRows = wrap.querySelector('.rq-ws-fees__custom-rows');
    dom.successPanel = wrap.querySelector('.rq-ws-success');

    /* ── Utilities ── */
    function genId() { return 'item_' + Math.random().toString(36).substr(2, 9); }

    var VENDOR_ALIASES = { 'ecore athletic': 'ECORE' };
    function normalizeVendor(v) {
      if (!v) return v;
      var key = v.toLowerCase().trim();
      return VENDOR_ALIASES[key] || v;
    }

    function fmt(n) {
      if (n == null || isNaN(n)) return '$0.00';
      return '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }

    function parseMoney(s) {
      if (!s) return 0;
      return parseFloat(String(s).replace(/[^0-9.\-]/g, '')) || 0;
    }

    /* ── Pricing Engine ── */
    function calculateDirect(cost, points) {
      if (!cost || points >= 100) return cost || 0;
      // CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-12) — only 0/blank returns cost
      // unchanged; NEGATIVE points discount below cost (cost / (1 - points/100)
      // with points<0 gives a denominator >1 → price under cost). Was `<= 0`,
      // which swallowed negatives and left the direct price stuck at cost.
      if (!points) return cost;
      return Math.round(cost / (1 - points / 100) * 100) / 100;
    }

    function reversePoints(cost, direct) {
      if (!direct || direct <= 0 || !cost) return 0;
      return (1 - cost / direct) * 100;
    }

    function autoPoints(cost, mapPrice) {
      if (!mapPrice || mapPrice <= 0 || !cost) return 0;
      return (1 - cost / mapPrice) * 100;
    }

    /* ── HTML Escaping ── */
    function escHtml(s) { var d = document.createElement('div'); d.textContent = s || ''; return d.innerHTML; }
    function escAttr(s) { return (s || '').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;'); }

    /* ── Custom Select Dropdown ── */
    function upgradeSelect(sel, extraClass) {
      var options = [];
      for (var i = 0; i < sel.options.length; i++) {
        if (sel.options[i].hidden) continue; // CUSTOM_AUTOTAX_BY_CLAUDE - a hidden option is a label for old data, never a choice
        options.push({ value: sel.options[i].value, label: sel.options[i].textContent });
      }
      var current = sel.value;
      var currentLabel = sel.options[sel.selectedIndex] ? sel.options[sel.selectedIndex].textContent : '';

      var container = document.createElement('div');
      container.className = 'rq-ws-custom-select' + (extraClass ? ' ' + extraClass : '');
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'rq-ws-custom-select__btn';
      btn.textContent = currentLabel;
      var drop = document.createElement('div');
      drop.className = 'rq-ws-custom-select__drop';
      options.forEach(function(opt) {
        var optBtn = document.createElement('button');
        optBtn.type = 'button';
        optBtn.className = 'rq-ws-custom-select__opt';
        optBtn.setAttribute('data-value', opt.value);
        optBtn.textContent = opt.label;
        drop.appendChild(optBtn);
      });
      container.appendChild(btn);
      container.appendChild(drop);

      if (sel.getAttribute('data-field')) container.setAttribute('data-field', sel.getAttribute('data-field'));
      if (sel.getAttribute('data-fee')) container.setAttribute('data-fee', sel.getAttribute('data-fee'));

      sel.parentNode.insertBefore(container, sel);
      sel.style.display = 'none';

      btn.addEventListener('click', function(e) {
        e.stopPropagation();
        wrap.querySelectorAll('.rq-ws-custom-select__drop--open').forEach(function(d) { if (d !== drop) d.classList.remove('rq-ws-custom-select__drop--open'); });
        drop.classList.toggle('rq-ws-custom-select__drop--open');
      });

      drop.addEventListener('click', function(e) {
        var optEl = e.target.closest('.rq-ws-custom-select__opt');
        if (!optEl) return;
        var val = optEl.getAttribute('data-value');
        btn.textContent = optEl.textContent;
        sel.value = val;
        sel.dispatchEvent(new Event('change', { bubbles: true }));
        drop.classList.remove('rq-ws-custom-select__drop--open');
      });

      document.addEventListener('click', function(e) {
        if (!container.contains(e.target)) drop.classList.remove('rq-ws-custom-select__drop--open');
      });

      container._select = sel;
      container._btn = btn;
      container._drop = drop;
      return container;
    }

    function syncCustomSelect(sel) {
      var df = sel.getAttribute('data-field');
      var dfee = sel.getAttribute('data-fee');
      var csWrap = df ? wrap.querySelector('.rq-ws-custom-select[data-field="' + df + '"]')
                  : dfee ? wrap.querySelector('.rq-ws-custom-select[data-fee="' + dfee + '"]')
                  : null;
      if (csWrap && csWrap._btn && csWrap._select) {
        var idx = csWrap._select.selectedIndex;
        csWrap._btn.textContent = (idx >= 0 && csWrap._select.options[idx])
          ? csWrap._select.options[idx].textContent : '';
      }
    }

    // Upgrade all existing selects
    wrap.querySelectorAll('.rq-ws-field__select').forEach(function(sel) { upgradeSelect(sel); });
    wrap.querySelectorAll('.rq-ws-fees__status').forEach(function(sel) { upgradeSelect(sel, 'rq-ws-custom-select--small'); });

    /* ── Find Item ── */
    function findItem(id) {
      for (var i = 0; i < state.items.length; i++) {
        if (state.items[i].id === id) return state.items[i];
      }
      return null;
    }

    /* ── Admin Detection ── */
    var isAdmin = (function() {
      try { return JSON.parse(sessionStorage.getItem('rep_portal_session') || '{}').admin === true; }
      catch(e) { return false; }
    })();

    /* ── Commission Eligibility ──
       CUSTOM_RFQPORTAL_BY_CLAUDE — Read earnsCommission from the session
       (populated by /rep-auth + /admin/impersonate). Used by the Deal
       Summary to render Commission as "—" instead of a projected dollar
       amount for reps who don't earn (admins managing quotes, etc.).
       Defaults to true for legacy sessions that pre-date this flag —
       keeps existing behavior intact until the rep re-logs in. */
    var repEarnsCommission = (function() {
      try {
        var s = JSON.parse(sessionStorage.getItem('rep_portal_session') || '{}');
        if (s && s.rep && typeof s.rep.earnsCommission === 'boolean') return s.rep.earnsCommission;
        if (typeof s.earnsCommission === 'boolean') return s.earnsCommission;
        return true;
      } catch(e) { return true; }
    })();

    /* ── Constants ── */
    var COMMISSION_GRID = {
      outbound: { 'Pay in Full': 0.37, '50/50': 0.37, '50/50 Net 30': 0.35, 'Net 30': 0.33 },
      inbound:  { 'Pay in Full': 0.27, '50/50': 0.27, '50/50 Net 30': 0.25, 'Net 30': 0.23 },
      shared:   { 'Pay in Full': 0.17, '50/50': 0.17, '50/50 Net 30': 0.15, 'Net 30': 0.13 }
    };
    var COMMISSION_BANDS = { outbound: '33–37%', inbound: '23–27%', shared: '13–17%' };

    /* ── Unit Toggle Products ──
       CUSTOM_RFQPORTAL_BY_CLAUDE — Products that support a per-piece ↔ per-sq-ft
       toggle on the QW row. Heritage Motivate (ECORE vinyl plank) ships per
       plank by default but reps need to be able to quote it per sq ft.
       To add a product: append an entry. titleMatch is case-insensitive regex
       (works for both RD handoff and catalog search since title is the most
       reliable identifier in both paths). */
    var UNIT_TOGGLE_PRODUCTS = [
      {
        titleMatch: /heritage\s+motivate/i,
        sqFtPerPiece: 2,
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-09-06) — ⛔ SOLD ONLY IN WHOLE CARTONS. ECORE
           ships Heritage in cartons of 12 planks (24 sq ft) and Ryan's rule is that we never buy
           a part carton, so a rep must not be able to sell one: *"if they tried to sell 23, it
           wouldn't let them."* The storefront quantity picker and the Gym Layout Designer already
           round to this same 12 (the product's `custom.qty_step` metafield); the Workstation was
           the one surface that accepted any number. Enforced in PIECES — in sq ft mode the step
           is piecesPerCarton x sqFtPerPiece (24) — by the qty snap in workstation-table.js and
           the save gate in workstation-save.js. */
        piecesPerCarton: 12,
        pieceLabelPlural: 'planks',
        pieceLabelSingular: 'plank',
        quotedAsPiece: 'Quoted as planks',
        quotedAsSqft: 'Quoted as sq ft'
      }
    ];

    /* The carton step for an item in its CURRENT unit mode, or 0 when the product has no carton
       rule. One place for the arithmetic so the snap and the save gate cannot disagree. */
    function cartonStepFor(item) {
      var cfg = getUnitToggleConfig(item);
      if (!cfg || !(cfg.piecesPerCarton > 1)) return 0;
      return item.unitMode === 'sqft' ? cfg.piecesPerCarton * (cfg.sqFtPerPiece || 1) : cfg.piecesPerCarton;
    }

    function getUnitToggleConfig(item) {
      if (!item || !item.title) return null;
      for (var i = 0; i < UNIT_TOGGLE_PRODUCTS.length; i++) {
        if (UNIT_TOGGLE_PRODUCTS[i].titleMatch.test(item.title)) return UNIT_TOGGLE_PRODUCTS[i];
      }
      return null;
    }

    /* ═══════════════════════════════════════════
       Create global namespace for all modules
       ═══════════════════════════════════════════ */
    window.QWS = {
      // Shared references
      state: state,
      dom: dom,
      wrap: wrap,
      API: API,
      Auth: Auth,
      isAdmin: isAdmin,
      repEarnsCommission: repEarnsCommission,

      // Module-private state exposed for cross-module access
      collapsedVendors: collapsedVendors,
      seriesQtyPropagated: seriesQtyPropagated,

      // Constants
      COMMISSION_GRID: COMMISSION_GRID,
      COMMISSION_BANDS: COMMISSION_BANDS,
      VENDOR_ALIASES: VENDOR_ALIASES,

      // Core utilities
      showToast: showToast,
      removeToast: removeToast,
      genId: genId,
      normalizeVendor: normalizeVendor,
      fmt: fmt,
      parseMoney: parseMoney,
      escHtml: escHtml,
      escAttr: escAttr,
      findItem: findItem,
      upgradeSelect: upgradeSelect,
      syncCustomSelect: syncCustomSelect,

      // Pricing engine
      calculateDirect: calculateDirect,
      reversePoints: reversePoints,
      autoPoints: autoPoints,

      // Unit-toggle (per-piece ↔ per-sq-ft)
      UNIT_TOGGLE_PRODUCTS: UNIT_TOGGLE_PRODUCTS,
      getUnitToggleConfig: getUnitToggleConfig,
      cartonStepFor: cartonStepFor
    };
  });
})();
